import * as React from 'react';
import { Box, Tooltip } from '@mui/material';

type Tone = 'success' | 'keep' | 'danger' | 'info' | 'warning' | 'neutral';

function verdictTone(verdict = ''): Tone {
  const v = verdict.toLowerCase();
  if (/(pause|turn off|kill|cut)/.test(v)) return 'danger';
  if (/(promote|retarget)/.test(v)) return 'info';
  if (/(flag|decide|divergen|watch|monitor)/.test(v)) return 'warning';
  if (/scale/.test(v)) return 'success';
  // "keep" gets its own teal so scale (grow it) vs keep (hold it) read
  // differently at a glance without shouting.
  if (/keep/.test(v)) return 'keep';
  if (/(winner|best)/.test(v)) return 'success';
  return 'neutral';
}

const chipBg: Record<Tone, string> = {
  success: 'var(--color-background-success, #e9f7ef)',
  keep: '#e6f2f5',
  danger: 'var(--color-background-danger, #fdecec)',
  info: 'var(--color-background-info, #e7f0fb)',
  warning: 'var(--color-background-warning, #fdf3e2)',
  neutral: 'var(--color-background-secondary, #f2f2f0)',
};
const chipFg: Record<Tone, string> = {
  success: 'var(--color-text-success, #1d7a4d)',
  keep: '#0e6a7b',
  danger: 'var(--color-text-danger, #c0392b)',
  info: 'var(--color-text-info, #185fa5)',
  warning: 'var(--color-text-warning, #854f0b)',
  neutral: 'var(--color-text-secondary, #5f5e5a)',
};

// Pinned left cell: colored verdict tag (verdict word + brief summary; full
// explanation on hover) + ad name + exact campaign name.
export default function VerdictCell({
  verdict,
  adName,
  adSuffix,
  campaignName,
  rationale,
  rationaleDetail,
}: {
  verdict?: string;
  adName?: string;
  // Disambiguator shown after the ad name when several ads share one name in a
  // table (e.g. duplicated creatives) — typically the ad_id tail.
  adSuffix?: string;
  campaignName?: string;
  rationale?: string;
  rationaleDetail?: string;
}) {
  const tone = verdictTone(verdict);
  const detail = rationaleDetail || rationale;

  const tag = verdict ? (
    <Box
      sx={{
        display: 'block',
        fontSize: 11.5,
        px: '8px',
        py: '4px',
        borderRadius: '8px',
        backgroundColor: chipBg[tone],
        color: chipFg[tone],
        whiteSpace: 'normal',
        lineHeight: 1.35,
        cursor: detail ? 'help' : 'default',
      }}
    >
      <Box
        component="span"
        sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}
      >
        {verdict}
      </Box>
      {rationale && <Box component="span">{` — ${rationale}`}</Box>}
    </Box>
  ) : null;

  return (
    <Box sx={{ py: '4px', lineHeight: 1.35, whiteSpace: 'normal' }}>
      {tag &&
        (detail ? (
          <Tooltip title={detail} arrow placement="top">
            {tag}
          </Tooltip>
        ) : (
          tag
        ))}
      <Box sx={{ fontSize: 12, fontWeight: 500, mt: tag ? '4px' : 0, whiteSpace: 'normal' }}>
        {adName}
        {adSuffix && (
          <Box
            component="span"
            sx={{ color: 'text.secondary', fontWeight: 400, fontSize: 11, ml: 0.5 }}
          >
            {adSuffix}
          </Box>
        )}
      </Box>
      {campaignName && (
        <Box
          sx={{
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: 11,
            color: 'text.secondary',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {campaignName}
        </Box>
      )}
    </Box>
  );
}
